import React from 'react';
import { createPortal } from 'react-dom';
import { ProfileTab } from './ProfileView';
import '../styles/components/profileMenuBar.css';

type FilterType = 'all' | 'today' | 'yesterday' | 'last_delete';

interface ProfileMenuBarProps {
  activeTab: ProfileTab;
  onTabChange: (tab: ProfileTab) => void;
  isAdmin: boolean;
  hasSubscriptionIssue?: boolean;
  // Trash-specific props
  trashFilter?: FilterType;
  onTrashFilterChange?: (filter: FilterType) => void;
  trashSelectedCount?: number;
  trashTotalCount?: number;
  onTrashSelectAll?: () => void;
  onTrashClearSelection?: () => void;
  onTrashRestore?: () => void;
  onTrashEmptyTrash?: () => void;
  onTrashEmptySelected?: () => void;
  trashRestoring?: boolean;
  trashEmptying?: boolean;
}

const ProfileMenuBar: React.FC<ProfileMenuBarProps> = ({ 
  activeTab, 
  onTabChange, 
  isAdmin, 
  hasSubscriptionIssue,
  trashFilter = 'all',
  onTrashFilterChange,
  trashSelectedCount = 0,
  trashTotalCount = 0,
  onTrashSelectAll,
  onTrashClearSelection,
  onTrashRestore,
  onTrashEmptyTrash,
  onTrashEmptySelected,
  trashRestoring = false,
  trashEmptying = false,
}) => {
  const [showManageMenu, setShowManageMenu] = React.useState(false);
  const [showFilterMenu, setShowFilterMenu] = React.useState(false);
  const [menuPosition, setMenuPosition] = React.useState({ top: 0, right: 0 });
  const [filterMenuPosition, setFilterMenuPosition] = React.useState({ top: 0, left: 0 });
  const manageMenuRef = React.useRef<HTMLDivElement>(null);
  const manageButtonRef = React.useRef<HTMLButtonElement>(null);
  const filterMenuRef = React.useRef<HTMLDivElement>(null);
  const filterButtonRef = React.useRef<HTMLButtonElement>(null);

  const getFilterLabel = () => {
    switch (trashFilter) {
      case 'all': return 'All';
      case 'today': return 'Today';
      case 'yesterday': return 'Yesterday';
      case 'last_delete': return 'Last Delete';
      default: return 'All';
    }
  };

  // Calculate menu positions
  React.useEffect(() => {
    if (showManageMenu && manageButtonRef.current) {
      const rect = manageButtonRef.current.getBoundingClientRect();
      const pos = {
        top: rect.bottom + 2,
        right: window.innerWidth - rect.right
      };
      setMenuPosition(pos);
    }
  }, [showManageMenu]);

  React.useEffect(() => {
    if (showFilterMenu && filterButtonRef.current) {
      const rect = filterButtonRef.current.getBoundingClientRect();
      const pos = {
        top: rect.bottom + 4,
        left: rect.left
      };
      setFilterMenuPosition(pos);
    }
  }, [showFilterMenu]);

  // Close menus when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (manageMenuRef.current && !manageMenuRef.current.contains(event.target as Node)) {
        setShowManageMenu(false);
      }
      if (filterMenuRef.current && !filterMenuRef.current.contains(event.target as Node) &&
          filterButtonRef.current && !filterButtonRef.current.contains(event.target as Node)) {
        setShowFilterMenu(false);
      }
    };

    if (showManageMenu || showFilterMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showManageMenu, showFilterMenu]);

  // If Trash tab is active, show trash-specific controls
  if (activeTab === 'trash') {
    return (
      <>
        <div className="transparent-menu-bar profile-menu-bar">
          {/* Period Filter - Dropdown */}
          <div className="trash-filter-selector">
            <button
              ref={filterButtonRef}
              className="trash-filter-btn"
              onClick={() => setShowFilterMenu(!showFilterMenu)}
              title="Change period"
            >
              {getFilterLabel()}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
          </div>

          {/* Manage Button with Dropdown */}
          <div className="manage-tab-wrapper">
            <button
              ref={manageButtonRef}
              className="menu-tab-btn"
              onClick={() => setShowManageMenu(!showManageMenu)}
            >
              Manage
              {trashSelectedCount > 0 && (
                <span className="menu-badge">{trashSelectedCount}</span>
              )}
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginLeft: '4px' }}>
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </button>
          </div>

          {/* Done Button */}
          <button
            className="menu-tab-btn"
            onClick={() => onTabChange('account')}
          >
            Done
          </button>
        </div>

        {/* Filter Dropdown - rendered via portal */}
        {showFilterMenu && createPortal(
          <div 
            ref={filterMenuRef}
            className="trash-filter-menu" 
            style={{ 
              position: 'fixed',
              top: `${filterMenuPosition.top}px`, 
              left: `${filterMenuPosition.left}px` 
            }}
          >
            {(['all', 'today', 'yesterday', 'last_delete'] as FilterType[]).map((f) => {
              const label = f === 'all' ? 'All' : 
                           f === 'today' ? 'Today' : 
                           f === 'yesterday' ? 'Yesterday' : 'Last Delete';
              return (
                <button
                  key={f}
                  className={`trash-filter-menu-item${trashFilter === f ? ' active' : ''}`}
                  onClick={() => {
                    onTrashFilterChange?.(f);
                    setShowFilterMenu(false);
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>,
          document.body
        )}

        {/* Manage Dropdown - rendered via portal */}
        {showManageMenu && createPortal(
          <div 
            ref={manageMenuRef}
            className="manage-dropdown" 
            style={{ top: `${menuPosition.top}px`, right: `${menuPosition.right}px` }}
          >
            {trashSelectedCount === 0 ? (
              <button 
                className="dropdown-item" 
                onClick={() => { onTrashSelectAll?.(); setShowManageMenu(false); }}
                disabled={trashTotalCount === 0}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 11l3 3L22 4"></path>
                  <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"></path>
                </svg>
                Select All
              </button>
            ) : (
              <button 
                className="dropdown-item" 
                onClick={() => { onTrashClearSelection?.(); setShowManageMenu(false); }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
                Clear Selection
              </button>
            )}
            
            <div className="dropdown-divider"></div>
            
            <button 
              className="dropdown-item dropdown-item-move" 
              onClick={() => { onTrashRestore?.(); setShowManageMenu(false); }}
              disabled={trashSelectedCount === 0 || trashRestoring}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                <path d="M21 3v5h-5" />
                <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                <path d="M3 21v-5h5" />
              </svg>
              Restore {trashSelectedCount > 0 ? `(${trashSelectedCount})` : ''}
            </button>

            <button 
              className="dropdown-item dropdown-item-delete" 
              onClick={() => { onTrashEmptySelected?.(); setShowManageMenu(false); }}
              disabled={trashSelectedCount === 0 || trashEmptying}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6" />
              </svg>
              Empty Selected {trashSelectedCount > 0 ? `(${trashSelectedCount})` : ''}
            </button>

            <button 
              className="dropdown-item dropdown-item-delete" 
              onClick={() => { onTrashEmptyTrash?.(); setShowManageMenu(false); }}
              disabled={trashTotalCount === 0 || trashEmptying}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2M10 11v6M14 11v6" />
              </svg>
              Empty All Trash
            </button>
          </div>,
          document.body
        )}
      </>
    );
  }

  // Regular profile tabs
  return (
    <div className="transparent-menu-bar profile-menu-bar">
      <button
        className={`menu-tab-btn ${activeTab === 'account' ? 'active' : ''}`}
        onClick={() => onTabChange('account')}
      >
        Account
      </button>

      <button
        className={`menu-tab-btn ${activeTab === 'subscription' ? 'active' : ''} ${hasSubscriptionIssue ? 'menu-tab-alert' : ''}`}
        onClick={() => onTabChange('subscription')}
      >
        Subscription
      </button>

      <button
        className="menu-tab-btn"
        onClick={() => onTabChange('trash')}
      >
        Trash
      </button>

      {isAdmin && (
        <>
          <button
            className={`menu-tab-btn ${activeTab === 'admin' ? 'active' : ''}`}
            onClick={() => onTabChange('admin')}
          >
            Admin
          </button>

          <button
            className={`menu-tab-btn ${activeTab === 'users' ? 'active' : ''}`}
            onClick={() => onTabChange('users')}
          >
            Users
          </button>

          <button
            className={`menu-tab-btn ${activeTab === 'stats' ? 'active' : ''}`}
            onClick={() => onTabChange('stats')}
          >
            Stats
          </button>

          <button
            className={`menu-tab-btn ${activeTab === 'queue' ? 'active' : ''}`}
            onClick={() => onTabChange('queue')}
          >
            Queue
          </button>
        </>
      )}

      {!isAdmin && (
        <button
          className={`menu-tab-btn ${activeTab === 'stats' ? 'active' : ''}`}
          onClick={() => onTabChange('stats')}
        >
          Stats
        </button>
      )}
    </div>
  );
};

export default ProfileMenuBar;
